import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/category/football', label: t('nav.categories') },
    { to: '/about', label: t('nav.about') },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group whitespace-nowrap"
          >
            <span className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-football-line text-lg text-background-50"></i>
            </span>
            <span className="text-xl font-bold font-heading text-foreground-950">
              {t('site.name')}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  isActive(link.to)
                    ? 'text-primary-600'
                    : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {link.label}
                {isActive(link.to) && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-primary-500 rounded-full"></span>
                )}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/about"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap px-3 py-2"
            >
              {t('nav.login')}
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors whitespace-nowrap px-5 py-2 rounded-md"
            >
              {t('nav.register')}
            </Link>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center text-foreground-700 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 py-4 px-4">
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-sm font-medium py-2 px-3 rounded-md transition-colors ${
                    isActive(link.to)
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-background-200/70 pt-3 mt-2 flex gap-2">
                <Link
                  to="/about"
                  className="flex-1 text-center text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 py-2 rounded-md transition-colors"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/about"
                  className="flex-1 text-center text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 py-2 rounded-md transition-colors"
                >
                  {t('nav.register')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}